import React from 'react';
import styled, { css } from 'styled-components';

/*
 * SectionBlock — paper-like wrapper for sheet sections.
 *
 * Renamed from SectionCard to avoid card-depth connotation per architect Q3.
 *  - No box-shadow, no rounded depth
 *  - Tone: neutral | red | green drives title-bar accent
 *  - fullWidth makes the section span both grid columns
 *  - print: break-inside avoid so sections never split mid-row across pages
 */

const Wrapper = styled.section`
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 0;
  break-inside: avoid;
  page-break-inside: avoid;
  ${p => p.fullWidth && css`
    grid-column: 1 / -1;
  `}

  @media print {
    border: 1px solid #000;
    background: #fff;
  }
`;

const TitleBar = styled.div`
  background: ${p => p.theme.colors.tone[p.tone]?.bg || 'transparent'};
  padding: ${p => p.theme.spacing[2]}px ${p => p.theme.spacing[3]}px;
  border-bottom: 1px solid ${p => p.theme.colors.border};
  display: flex;
  align-items: baseline;
  gap: ${p => p.theme.spacing[2]}px;
`;

const TitleTa = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: ${p => p.theme.typography.weights.semibold};
  color: ${p => {
    const t = p.theme.colors.tone[p.tone];
    return (!t || t.text === 'inherit') ? p.theme.colors.text.primary : t.text;
  }};
  font-family: 'Noto Sans Tamil', ${p => p.theme.typography.fontFamily};

  @media (max-width: ${p => p.theme.breakpoints.mobile}px) {
    font-size: 16px;
  }
`;

const TitleEn = styled.span`
  font-size: 13px;
  font-weight: ${p => p.theme.typography.weights.regular};
  color: ${p => p.theme.colors.text.secondary};

  @media print {
    color: #000;
  }
`;

const ToneIcon = styled.span`
  font-size: 14px;
  color: ${p => {
    const t = p.theme.colors.tone[p.tone];
    return (!t || t.text === 'inherit') ? p.theme.colors.text.primary : t.text;
  }};
  margin-right: 2px;
  display: inline-block;
`;

const Body = styled.div`
  padding: ${p => p.theme.spacing[2]}px 0;
`;

const TONE_GLYPHS = {
  red:     '⊖', // ⊖
  green:   '⊕', // ⊕
  neutral: '',
};

const SectionBlock = ({
  titleTa,
  titleEn,
  tone = 'neutral',
  fullWidth = false,
  children,
  className,
}) => {
  const glyph = TONE_GLYPHS[tone];

  return (
    <Wrapper fullWidth={fullWidth} className={className}>
      <TitleBar tone={tone}>
        {glyph && <ToneIcon tone={tone} aria-hidden="true">{glyph}</ToneIcon>}
        <TitleTa tone={tone}>{titleTa}</TitleTa>
        <TitleEn>{titleEn}</TitleEn>
      </TitleBar>
      <Body>{children}</Body>
    </Wrapper>
  );
};

export default SectionBlock;
